import { themeGet } from '@styled-system/theme-get';
import { v4 as uuidv4 } from 'uuid';
import { Tooltip } from 'antd';
import _, { max, min, uniqBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { ScaleLoader } from 'react-spinners';
import styled, { useTheme } from 'styled-components';
import { Button } from '../../../components/Button/Button';
import { EditableInputDisplay } from '../../../components/EditableInputDisplay/EditableInputDisplay';
import { CoveringLoader } from '../../../components/LoadingSpinner/CoveringLoader';
import { IFieldlessAllocationResponse, IFieldlessRankingSeed } from '../../../logic/Models/Responses/FieldlessSeedRankingResponse';
import { UserPermission } from '../../../logic/Models/Responses/UserPermissions';
import { convertProductPlanToFieldPlan } from '../../../logic/store/Plans/FieldPlan/FieldPlanThunks';
import { createProductPlan, getFieldlessSeedAllocations, getFieldlessSeedRankings, getProductGamePlan, putProductPlan } from '../../../logic/store/Plans/ProductGamePlanActions';
import { 
	addProductLine,
	addSeries,
	createNewPlan,
	loadPlanForEditing,
	planEditsToApi,
	recommendationOptionsKey,
	removeProductLine,
	removeSeries,
	resetRecommendationOptions,
	updateProductLine,
	updateRecommendationOptions,
	updateTreatment,
	setSelectedPlanId
} from '../../../logic/store/Plans/ProductGamePlanSliceV7';
import { setSelectedPlanId as setUiSelectedPlanId } from '../../../logic/store/UI/UISlice';
import { CropConfig, cornId, soyId } from '../../../logic/store/Seeds/CropsSlice';
import { useAppDispatch, useAppSelector } from '../../../logic/store/Store';
import { getAllPlantingRates, isLoadingPlantingRates } from '../../../logic/store/Seeds/PlantingRateSlice';
import { hasEffectivePermission } from '../../../logic/store/User/AuthSlice';
import { useProductRollup } from '../../../logic/Utility/Pricing/useProductRollup';
import { useAmplitudePassthrough, useDynamicEvent } from '../../../logic/Utility/useAmplitude';
import { EventAcreProposalNames, EventNames } from '../../../tracing/EventNames';
import { EventGrowerTags, EventPlanTags, EventSelectionTags, EventStructureTags, EventTags } from '../../../tracing/EventTagNames';
import { useScopedSession } from '../../../tracing/session';
import { cropBrandAssociations, ICropBrandAssociation } from '../CropBrandAssociation';
import { CropTabBar } from '../CropTabBar';
import { ProductPlanHeaderRow } from './ProductPlanHeaderRow';
import { IPlanProductListProps, PlanProductList } from './PlanProductList';
import { ProductRow } from './ProductRow';
import { ProductSummaryHeader } from './ProductSummaryHeader';
import { SeriesRow } from './SeriesRow';
import { EditRecommendationOptions } from './EditRecommendationOptions';
import { IGrowerResponse } from '../../../logic/Models/Responses/GrowerResponse';
import { IOrderLine } from '../../../logic/Models/Responses/OrderResponse';
import { PriorYearSeedList } from './PriorYearSeedList';
import { dynamicSort } from '../../../logic/Utility/Utils';
import { AgronomicsModal } from './AgronomicsModal';
import { useAsyncThunkListener } from '../../../logic/Utility/useAsyncThunkListener';
import { AvailabilityIcon } from './AvailabilityIcon';
import { BrandLookup, HybridLookup, SeriesLookup, treatmentKey } from '../../../logic/store/Seeds/SeedLookups';
import { AppliedProductFilters, filtersFromRecOptions } from './AppliedProductFilters';
import { ApplyRecommendationModal } from './ApplyRecommendationModal';
import { filterObject, mapObject } from '../../../logic/Utility/mapObject';
import { AvailabilityIndicator } from '../../../logic/Models/Responses/SeedResponseV7';
import { ConvertModal } from './ConvertModal';
import { getGrowerOrdersForYear } from '../../../logic/store/Grower/OrderThunks';
import { IRecommendationOptions } from '../../../logic/store/Plans/IRecommendationOptions';
import { RelatedObjects } from '../../../logic/Utility/Pricing/ProductRollupCalcLogger';
import { getLocalPositioning } from '../../../logic/store/Seeds/SeedsActions';
import { LocalPositioningDrawer } from './LocalPositioningDrawer';
import { getCustomTreatments } from '../../../logic/store/Seeds/TreatmentActions';
import { startPlanPdf } from '../../../logic/Utility/registerCancelableThunks';
import { downloadPlanPdf, getPlans } from '../../../logic/store/Grower/PlanThunks';
import { clearPlanCompetitiveAllowance } from '../../../logic/store/Grower/GrowerSlice';

interface IProductGamePlanV7Props
{
	isGoldenHarvestUser: boolean;
}

/**
 * Redux connected page component for product plan.
 *
 * Ideally no descendant components should be connected (ie no mapStateToProps and no useSelector).
 */
export const ProductGamePlanV7 = ({ isGoldenHarvestUser }: IProductGamePlanV7Props) =>
{
	const theme = useTheme();
	const history = useHistory();
	const dispatch = useAppDispatch();

	const seeds = useAppSelector(s => s.seeds.allSeedsV7);
	const seedsIsLoading = useAppSelector(s => s.seeds.isLoading);
	// Assume even if there is no errorMessage, that no loading and no data is an error state.
	// This is to detect when the top level error modal cleared the error, but there is no ongoing operation to fetch the seeds again.
	const seedsError = useAppSelector(s => s.seeds.errorMessage || (!s.seeds.isLoading && s.seeds.allSeedsV7 === undefined));

	const brandLookup = useAppSelector(s => s.seeds.brands);
	const seriesLookup = useAppSelector(s => s.seeds.series);
	const hybridLookup = useAppSelector(s => s.seeds.hybrids);
	const treatmentLookup = useAppSelector(s => s.seeds.treatments);

	// Permissions
	const allowPricing = useAppSelector(s => hasEffectivePermission(s, UserPermission.CanSeePricing));
	const allowProductPlanRecommendationPermission = useAppSelector(s => hasEffectivePermission(s, UserPermission.CanSeeProductPlanRecommendations));
	const userIsGrower = useAppSelector(s => hasEffectivePermission(s, UserPermission.IsGrower, true));
	const allowFieldProposal = useAppSelector(s => hasEffectivePermission(s, UserPermission.CanUseFieldProposal));

	const { calculator } = useProductRollup(seeds ?? []);

	const [cropBrand, setCropBrand] = useState<ICropBrandAssociation>(cropBrandAssociations[0]); // currentRecommendation
	const [isConvertingModalOpen, setConvertingModalOpen] = useState(false);
	const [isAgronomicInputsModalOpen, setIsAgronomicInputsModalOpen] = useState(false);
	const [showRunRecWarning, setShowRunRecWarning] = useState(false);

	const currentCropYear = useAppSelector(s => s.crops.CropYear);

	// Local Positioning
	const [showLocalPositioningDrawer, setShowLocalPositioningDrawer] = useState(false);
	const [localPositioningSeriesId, setLocalPositioningSeriesId] = useState<string | undefined>(undefined);

	// Get the grower so we can get its Zip.
	const growers = useAppSelector(s => s.grower.Growers);
	const selectedGrowerId = useAppSelector(s => s.ui.SelectedGrowerId);
	const selectedGrower = useMemo(
		() => growers.find(g => g.Id === selectedGrowerId)!,
		[growers, selectedGrowerId]
	);
	const isCanadianGrower = /^[A-Za-z][0-9][A-Za-z][ -]?[0-9][A-Za-z][0-9]$/.test(selectedGrower?.Zip ?? '');
	const allowProductPlanRecommendation = allowProductPlanRecommendationPermission && !isCanadianGrower;
	const growerHasFields = useMemo(
		() => (selectedGrower?.Farms?.flatMap(f => f.Fields).length ?? 0) > 0,
		[selectedGrower]
	);

	// If you arrive on this page and there is no selected plan, we enter 'new plan' mode. Otherwise we load the selected plan.
	const { plan, planId, session, savePlan } = useLoadOrCreatePlan(!seedsError, selectedGrower, brandLookup);

	const recommendationOptions = plan?.edits?.recommendationOptions[recommendationOptionsKey(cropBrand)];
	const selectedProducts = plan?.edits?.products;
	const selectedProductsCropBrand = useMemo(
		() => 
		{
			if (!brandLookup || !seriesLookup || !selectedProducts)
				return [];

			return Object.values(selectedProducts)
				.filter(p => seriesMatchesCropBrand(brandLookup, seriesLookup[p.seriesId], cropBrand));
		},
		[brandLookup, seriesLookup, cropBrand, selectedProducts]
	);
	const selectedSeriesList = useMemo(
		() => seriesLookup && selectedProducts ? uniqBy(Object.values(selectedProducts), p => p.seriesId).map(p => seriesLookup[p.seriesId]) : [], 
		[selectedProducts]
	);

	/** Total number of series in this plan. */
	const cropBrandSeriesCount = useMemo(
		() => selectedProducts ? 
			new Set(selectedProductsCropBrand).size : 0, 
		[selectedProductsCropBrand]
	);

	const summary = useMemo(
		() => 
		{
			const rollup = selectedProductsCropBrand && selectedProductsCropBrand.length > 1
				? calculator.calculate(Object.values(selectedProductsCropBrand).map(p => ({ 
					...p, 
					targetYield: p.targetYield ?? 0, 
					seedingRate: p.seedingRate ?? 0, 
					appliedAcres: p.appliedAcres ?? 0,
				}))).planRollup 
				: undefined;

			if (!rollup)
				return undefined;

			// TargetYield is not valid for soy.
			return {
				...rollup,
				avgTargetYield: cropBrand.cropId === soyId ? undefined : rollup.avgTargetYield, 
			};
		},
		[calculator, selectedProductsCropBrand, cropBrand]
	);

	const seedRankingForFilters = useAsyncThunkListener(getFieldlessSeedRankings);

	/** All series filtered down to ones that are selectable. */
	const selectableSeriesLookup = useMemo<SeriesLookup | undefined>(
		() => brandLookup && seriesLookup 
			? filterObject(seriesLookup, (s, k) => 
				s.HasAvailableTreatment 
				&& s.CropId === cropBrand.cropId 
				&& seriesMatchesCropBrand(brandLookup, s, cropBrand)
			) as SeriesLookup
			: undefined,
		[brandLookup, seriesLookup, cropBrand]
	);
	
	/** Additional sorting and transform into expected type for ProductList */
	const selectableProductList = useMemo<IPlanProductListProps['seriesList']>(
		() => sortSelectableSeries(
			hybridLookup, 
			selectableSeriesLookup, 
			recommendationOptions, 
			seedRankingForFilters.data?.rankings
		),
		[selectableSeriesLookup, seedRankingForFilters]
	);
	
	/** The min and max RM of all seeds. */
	const rmMinMax = useMemo(() => 
	{
		if (!selectableSeriesLookup || Object.values(selectableSeriesLookup).length === 0)
			return { min: 0, max: 200 };

		const allRms = Object.values(selectableSeriesLookup)
			.map(s => Number.parseFloat(s.RelativeMaturity));

		return {
			min: Math.max(min(allRms)!, 0), // Never let it go below 0.
			max: Math.min(max(allRms)!, 200), // Has to at least be 200.
		};
	}, [selectableSeriesLookup]);

	const userCanSeeEnogenBrand = useAppSelector(s => hasEffectivePermission(s, UserPermission.CanSeeEnogenBrand) 
		|| hasEffectivePermission(s, UserPermission.CanSeeEnogenCanadaBrand));

	// Grab last year's orders for showing prior year's seed list
	useEffect(() =>
	{
		if (currentCropYear && selectedGrower.SalesforceId && selectedGrower.FoundationId)
		{
			const cropYear = Number.parseInt(currentCropYear);
			// Retrieve the orders for last crop year
			dispatch(getGrowerOrdersForYear({
				growerId: selectedGrowerId,
				year: cropYear - 1
			}));
		}
	}, [selectedGrowerId, currentCropYear, ]);

	const allLastYearSeeds = useMemo(
		() => lastYearGrowerSeeds(selectedGrower, cropBrand, Number.parseInt(currentCropYear)), 
		[selectedGrower, cropBrand, currentCropYear]
	);

	const { 
		available: lastYearAvailableSeeds, 
		unavailable: lastYearUnavailableSeeds 
	} = useMemo(
		() => hybridLookup 
			? groupLastYearSeedsByAvailability(hybridLookup, allLastYearSeeds)
			: { available: [], unavailable: [] }, 
		[hybridLookup, allLastYearSeeds]
	);

	const uniqueTraits = useMemo(() => 
	{
		if (!hybridLookup || !selectableSeriesLookup) return [];

		// The names we want to float to the top of the array
		// TODO: 07/07/2022 - ELM - Change this to use a set of trait names from the client config in the future
		const primaryTraitNames = ['D', 'V', 'DV'];

		return uniqBy(
			Object.values(hybridLookup)
				.filter(h => !!selectableSeriesLookup[h.SeriesId] && h.IsAvailable)
				.map(h => ({
					traitName: h.TraitName,
					traitId: h.TraitId,
					traitFullName: h.TraitFullName,
				})), 
			h => h.traitId
		)
			.sort(dynamicSort('traitName', 'descending'))
			// Float primary names up to the top.
			.sort((a,b) => 
			{
				if (primaryTraitNames.includes(a.traitName))
					return -1;
				if (primaryTraitNames.includes(b.traitName))
					return 1;
				else
					return 0;
			});
	}, [selectableSeriesLookup, hybridLookup]);
	
	// Validation before allowing the user to click run recommendation.
	const runRecommendationError = useMemo(
		() =>
			selectableProductList.length === 0 
				? 'No seeds available for selection'
				: (!recommendationOptions?.zip || recommendationOptions?.zip === '') 
					? 'Please enter a zip code to retrieve seeds.' 
					: (recommendationOptions?.acres == 0) 
						? 'Please add acreage to retrieve seeds.'
						: undefined, 
		[recommendationOptions]
	);

	const creatingPlan = useAsyncThunkListener(createProductPlan);
	const updatingPlan = useAsyncThunkListener(putProductPlan);
	const convertingPlan = useAsyncThunkListener(convertProductPlanToFieldPlan);

	const allProductsSaveReady = useMemo(() =>
		selectedProducts && Object.keys(selectedProducts).length > 0 
			? Object.values(selectedProducts).every(p => p.appliedAcres && p.quantity && p.seedingRate)
			: false, 
	[selectedProducts]);

	// Planting rates are loaded once on grower change, but we check for its errors just in case.
	// Errors are handled inside the target yield input on the product row.
	const plantingRateError = useAppSelector(s => s.plantingRate.error);
	const plantingRates = useAppSelector(s => s.plantingRate.rates);
	const plantingRatesIsLoading = useAppSelector(isLoadingPlantingRates);


	// When the seed list changes, set the rec options to min and max rms of all the seeds.
	useEffect(() => 
	{
		const valueAlreadySetAndInRange = !!recommendationOptions &&
			(recommendationOptions.rmMin !== undefined && recommendationOptions.rmMin >= rmMinMax.min)
			&& (recommendationOptions.rmMax !== undefined && recommendationOptions.rmMax <= rmMinMax.max);
		
		if (!recommendationOptions || valueAlreadySetAndInRange) 
			return;

		dispatch(updateRecommendationOptions({
			planId: planId,
			cropId: cropBrand.cropId,
			brandApplication: cropBrand.brandApplication,
			recommendationOptions: {
				rmMin: rmMinMax.min,
				rmMax: rmMinMax.max,
			}
		}));
	}, [recommendationOptions, cropBrand, rmMinMax]);

	// On start, load initial seed ranking data if rec has filters applied.
	useEffect(() =>
	{
		if (!recommendationOptions
			|| !selectedGrower
			|| seedRankingForFilters.isLoading
			|| seedRankingForFilters.isError
			|| isCanadianGrower
		)
			return;

		const rankingBrand = seedRankingForFilters.data?.rankings[0].Brand.toLowerCase();
		const brandTab = cropBrand.brandApplication.toLowerCase();
		const hasCorrectData = !!seedRankingForFilters.data && (
			brandTab === 'any' 
				? rankingBrand !== 'enogen'
				: rankingBrand === brandTab
		);
		if (hasCorrectData)
			return;

		dispatch(getSeedListFilteredRankings(cropBrand, {
			...recommendationOptions,
			// API will return all seeds when no traits are selected.
			selectedTraits: [], 
		}));
	}, [seedRankingForFilters, recommendationOptions, cropBrand, isCanadianGrower]);

	const productFilters = useMemo(
		() => recommendationOptions ? filtersFromRecOptions(uniqueTraits, recommendationOptions) : undefined,
		[uniqueTraits, recommendationOptions]
	);

	const onAgModalApply = () =>
	{
		if (!recommendationOptions)
			return;

		dispatch(updateRecommendationOptions({
			planId: planId,
			cropId: cropBrand.cropId,
			brandApplication: cropBrand.brandApplication,
			recommendationOptions: {
				isFilterApplied: true,
			}
		}));

		// Close the modal
		setIsAgronomicInputsModalOpen(false);
	};
	
	const onAgModalClear = () => 
	{
		dispatch(resetRecommendationOptions({
			planId,
			cropId: cropBrand.cropId,
			brandApplication: cropBrand.brandApplication,
		}));

		setIsAgronomicInputsModalOpen(false);
	};

	/** Convenience hook for updating recommendation options */
	const useRecOptionsUpdater = function <TValue extends number | string | Array<any> | void>(
		amplitudeEvent: EventNames,
		valueUpdater: (value: TValue) => Partial<IRecommendationOptions>,
		valueUpdaterDeps: any[] = [],
	) 
	{
		return useAmplitudePassthrough(
			session,
			amplitudeEvent,
			((value: TValue) => 
			{
				dispatch(updateRecommendationOptions({
					planId,
					cropId: cropBrand.cropId,
					brandApplication: cropBrand.brandApplication,
					recommendationOptions: {
						...valueUpdater(value),
						isFilterApplied: true,
					},
				}));
			}),
			[cropBrand, planId, ...valueUpdaterDeps]
		);
	};
	const recOptionsUpdateZipCode = useRecOptionsUpdater(
		EventAcreProposalNames.UpdateRecommendationZipCode, 
		(v: string)=> ({ zip: v })
	);
	const recOptionsUpdatePerformance = useRecOptionsUpdater( 
		EventAcreProposalNames.UpdateRecommendationPerf, 
		(v: number) => ({ performance: v })
	);
	const recOptionsUpdateTopEndYield = useRecOptionsUpdater( 
		EventAcreProposalNames.UpdateRecommendationTopEndYield, 
		(v: number) => ({ topEndYield: v })
	);
	const recOptionsUpdateSeedsRequired = useRecOptionsUpdater( 
		EventAcreProposalNames.UpdateRecommendationSeedsRequired, 
		(v: number) => ({ seedsRequired: v })
	);
	const recOptionsUpdateTargetYield = useRecOptionsUpdater( 
		EventAcreProposalNames.UpdateRecommendationTargetYield, 
		(v: number) => ({ targetYield: v })
	);
	const recOptionsUpdateAcres = useRecOptionsUpdater( 
		EventAcreProposalNames.UpdateRecommendationAcres, 
		(v: number) => ({ acres: v })
	);
	const recOptionsUpdateIrrigation = useRecOptionsUpdater( 
		EventAcreProposalNames.UpdateRecommendationIrrigation, 
		() => ({ irrigated: !recommendationOptions!.irrigated }), 
		[recommendationOptions]
	);
	const recOptionsUpdateFungicide = useRecOptionsUpdater( 
		EventAcreProposalNames.UpdateRecommendationFungicide, 
		() => ({ fungicide: !recommendationOptions!.fungicide }), 
		[recommendationOptions]
	);
	const recOptionsUpdateRmMax = useRecOptionsUpdater( 
		EventAcreProposalNames.UpdateRecommendationRmMax, 
		(v: number) => ({ rmMax: Math.min(v,rmMinMax.max) })
	);
	const recOptionsUpdateRmMin = useRecOptionsUpdater( 
		EventAcreProposalNames.UpdateRecommendationRmMin, 
		(v: number) => ({ rmMin: Math.max(v, rmMinMax.min) })
	);
	const recOptionsUpdateRmMinMax = useRecOptionsUpdater(
		EventAcreProposalNames.UpdateRecommendationRmMaxAndMin, 
		(v: [number,number]) => ({ rmMin: Math.max(v[0], rmMinMax.min), rmMax: Math.min(v[1],rmMinMax.max) })
	);
	const recOptionsUpdateTraitList = useRecOptionsUpdater( 
		EventAcreProposalNames.UpdateRecommendationTraits, 
		(v: string[]) => ({ selectedTraits: v })
	);
	
	const fetchAndApplyRecommendation = useAmplitudePassthrough(session, EventAcreProposalNames.RunFieldlessRecommendation, async () =>
	{
		if (!recommendationOptions)
			return;

		// This will bring up the global loading spinner.
		const response = await dispatch(getFieldlessSeedAllocations({
			acres: recommendationOptions.acres,
			consistencyPerf: recommendationOptions.performance,
			cropAssociation: recommendationOptionsKey(cropBrand),
			enogenOnly: cropBrand.brandApplication === 'Enogen',
			cropId: cropBrand.cropId,
			fungicide: recommendationOptions.fungicide,
			irrigated: recommendationOptions.irrigated,
			isFilterApplied: recommendationOptions.isFilterApplied,
			rmMax: recommendationOptions.rmMax,
			rmMin: recommendationOptions.rmMin,
			seedsRequested: recommendationOptions.seedsRequired,
			selectedTraits: recommendationOptions.selectedTraits,
			topEndYield: recommendationOptions.topEndYield,
			zipCode: recommendationOptions.zip
		}));

		if (response && response.meta.requestStatus === 'fulfilled')
		{
			const results: IFieldlessAllocationResponse = response.payload as unknown as IFieldlessAllocationResponse;
			
			for (const product of selectedProductsCropBrand)
			{
				// Clear existing seeds for this cropbrand (aka tab).
				dispatch(removeProductLine({
					planId,
					key: product.key,
				}));
			}

			// Bail if there are no rec results. But still clear the list as we don't want to imply their old selection was the rec
			if (!results?.Items)
				return;

			// Add recommended products.
			for (const rr of results?.Items)
			{
				dispatch(addSeries({ 
					planId: planId, 
					seriesId: rr.SeedWithRank.Id,
					plantingRates,
					line: {
						appliedAcres: rr.Acres,
						targetYield: recommendationOptions.targetYield,
					}
				}));
			}
		}
	},[cropBrand, selectedGrower, planId, recommendationOptions, plantingRates, selectedProductsCropBrand]);

	const onSave = useAmplitudePassthrough(session, EventAcreProposalNames.SaveAcreProposal, savePlan, [savePlan]);

	/** The action when a user confirms that they want to convert.  Will save the current plan and then convert it to a product plan. */
	const onConvert = useAmplitudePassthrough(session, EventAcreProposalNames.ConvertAcreProposalToFieldPlan, async () => 
	{
		// Make sure the plan exists and/or is fully updated.
		const savedProductPlanId = await savePlan();
		
		const response = await dispatch(convertProductPlanToFieldPlan({ productPlanId: savedProductPlanId })).unwrap();
		const newPlanId = response?.Id;
		if(newPlanId)
		{
			setConvertingModalOpen(false);
			await dispatch(setUiSelectedPlanId(newPlanId));
			history.push('/fieldactivities/fieldplan');
		}
	}, [plan, planId, setSelectedPlanId, history, selectedGrowerId, brandLookup,]);

	const pdfDownload = useAsyncThunkListener(downloadPlanPdf);
	const onDownloadPdf = async () => 
	{
		const savedPlanId = await savePlan();

		dispatch(startPlanPdf({ 
			PlanId: savedPlanId, 
			CoverConfig: undefined 
		}));
	};

	// Save, then open the Pricing page (which reads ui.SelectedPlanId from grower.Plans).
	const onOpenPricing = async () =>
	{
		const savedPlanId = await savePlan();
		// grower.Plans lacks a just-created plan until getPlans runs.
		if (!selectedGrower?.Plans?.some(p => p.Id === savedPlanId))
		{
			await dispatch(getPlans({ cropYear: currentCropYear }));
		}
		dispatch(clearPlanCompetitiveAllowance({ planId: savedPlanId, growerId: selectedGrowerId }));
		dispatch(setUiSelectedPlanId(savedPlanId));
		history.push('/fieldactivities/pricing', { from: '/fieldactivities/productplan' });
	};
	const listedPlanStatus = selectedGrower?.Plans?.find(p => p.Id === planId)?.Status;

	const onAddOldSeed = useAmplitudePassthrough(
		session, 
		EventAcreProposalNames.AddPreviouslyOrderedSeriesAndHybrid, 
		(oldSeed:IOrderLine) =>
		{
			const hybridId = oldSeedHybridId(oldSeed);
			dispatch(addSeries({ planId: planId, seriesId: oldSeed.SeriesId, hybridId, plantingRates, treatmentId: oldSeed.TreatmentId }));
		},
		[planId, plantingRates]
	);

	const addTraitToSeries = useAmplitudePassthrough(
		session, 
		EventAcreProposalNames.AddNewTraitToSeries, 
		(hybridId: string) => dispatch(addProductLine({ planId, hybridId })), 
		[planId]
	);

	const openLocalPositioningDrawer = (selectedSeriesId: string) =>
	{
		setLocalPositioningSeriesId(selectedSeriesId);
		setShowLocalPositioningDrawer(true);
	};

	const closeLocalPositioningDrawer = () =>
	{
		setLocalPositioningSeriesId(undefined);
		setShowLocalPositioningDrawer(false);
	};
	
	/** List of all loading messages. */
	const loadings = {
		'Fetching seeds': seedsIsLoading,
		'Saving plan': creatingPlan.isLoading || updatingPlan.isLoading,
		'Converting plan': convertingPlan.isLoading,
		'Generating PDF': pdfDownload.isLoading,
		'Loading plan': !plan?.edits,
		'Fetching planting rates': plantingRatesIsLoading,
	};

	// Prioritize showing the error message for loading seeds even if there's something else continuing to load.
	const anythingLoading = !seedsError && Object.values(loadings).some(l => l);

	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				paddingLeft: 70, 
				paddingRight: 60, 
				zIndex:998,
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<ProductPlanHeaderRow 
				growerName={selectedGrower.Name}
				growerZip={selectedGrower.Zip}
				growerState={selectedGrower.State}
				onSave={onSave}
				disableSave={
					(!selectedProducts || Object.values(selectedProducts).length === 0)
						? 'You must add a product to your Proposal in order to save it.' 
						: !allProductsSaveReady
							? 'All products must have valid applied area, quantity, and seeding rate.' 
							: (creatingPlan.isLoading || updatingPlan.isLoading || convertingPlan.isLoading)
								? 'Saving plan...'
								: undefined
				}
				onConvert={() => setConvertingModalOpen(true)}
				disableConvert={
					!allowFieldProposal
						? 'You do not have access to this feature'
						: !growerHasFields 
							? 'This option is only available if fields are uploaded to a grower\'s account'
							: (!selectedProducts || Object.values(selectedProducts).length === 0)
								? 'You must add a product to your Plan in order to place it on fields.' 
								: !allProductsSaveReady
									? 'All products must have valid applied area, quantity, and seeding rate.' 
									: (creatingPlan.isLoading || updatingPlan.isLoading || convertingPlan.isLoading)
										? 'Cannot convert while plan is loading or another operation is underway.'
										: undefined
				}
				onPricing={allowPricing ? onOpenPricing : undefined}
				disablePricing={
					listedPlanStatus === 'Order'
						? 'Pricing is not available for an ordered proposal.'
						: (!selectedProducts || Object.values(selectedProducts).length === 0)
							? 'You must add a product to your Proposal in order to save then open Pricing.'
							: !allProductsSaveReady
								? 'All products must have valid applied area, quantity, and seeding rate.'
								: (creatingPlan.isLoading || updatingPlan.isLoading || convertingPlan.isLoading)
									? 'Saving plan...'
									: undefined
				}
				onPdfDownload={onDownloadPdf}
				disablePdfDownload={
					!allProductsSaveReady
						? 'You must add a product to your Proposal in order to save then download the PDF'
						: (creatingPlan.isLoading || updatingPlan.isLoading || convertingPlan.isLoading) 
							? 'Cannot download pdf while plan is loading or another operation is underway.' 
							: undefined
				}
			/>
			<ProductPlanRecTabs>
				{cropBrandAssociations
					.filter(cba => userCanSeeEnogenBrand ? true : cba.brandApplication !== 'Enogen')
					.map(cba => (
						<CropTabBar
							key={cba.cropId + cba.brandApplication}
							label={cba.title}
							tabWidth={'95px'}
							selected={cba.cropId === cropBrand.cropId && cba.brandApplication == cropBrand.brandApplication}
							onClick={() => setCropBrand({...cba})} 
						/>
					))}
			</ProductPlanRecTabs>
			<ProductPlanEditorContent className='ProductPlanEditorContent'>
				{(!anythingLoading && plan?.edits && brandLookup && hybridLookup && treatmentLookup && selectedProducts) && (
					<>
						<PlanProductList
							planId={planId}
							recommendation={cropBrand}
							seriesList={selectableProductList}
							isGoldenHarvestUser={isGoldenHarvestUser}
							onAddSeries={(series) =>
							{
								dispatch(addSeries({ planId: planId, seriesId: series.id }));
							}}
						/>
						<div className='MainEditorVerticalStack'>
							<div className='MainEditorTitle'>
								{`${cropBrand.title} ${allowPricing ? 'Pricing and ' : ''}Units`}
								{allowProductPlanRecommendation && !!recommendationOptions && (
									<div style={{ marginLeft: 'auto' }}>
										<EditableInputDisplay
											className={'ZipCodeEntry'}
											validationTooltip={'Zip Code is not a valid format.'}
											description={<>{plan.isNewPlan && <span style={{ color: theme.colors.primary }}>New&nbsp;</span>} Recommendation for</>}
											inputValue={recommendationOptions.zip}
											inputType='number'
											validateInput={validateZipCode}
											saveInput={recOptionsUpdateZipCode}
											disableEdit={userIsGrower}
										/>
									</div>
								)}
							</div>
							{summary && (
								<ProductSummaryHeader 
									acres={summary.area}
									totalBags={summary.totalBags}
									seedsPerAcre={summary.avgSeedingRate}
									targetYield={summary.avgTargetYield}
									allowPricing={allowPricing}
									costPerAcre={summary.avgCostPerAcre}
									totalCost={summary.discountedTotalCost}
								/>
							)}
							<div className='MainEditorContent'>
								{allowProductPlanRecommendation && !!recommendationOptions && (
									<EditRecommendationOptions 
										options={recommendationOptions}
										cropBrand={cropBrand}
										cropConfig={CropConfig()[cropBrand.cropId]}
										onUpdateAcres={recOptionsUpdateAcres}
										onUpdateSeedsRequired={recOptionsUpdateSeedsRequired}
										onUpdateTargetYield={recOptionsUpdateTargetYield}
										buttonArea={
											<Tooltip title={runRecommendationError} placement='top'>
												<span>
													<Button
														className='ShowSeeds_Button'
														style={{ height: 40, width: 200 }}
														variant='outlined'
														onClick={() => 
														{
															if (cropBrandSeriesCount > 0)
																setShowRunRecWarning(true);
															else 
																fetchAndApplyRecommendation();
														}}
													>
													Run Recommendation
													</Button>
												</span>
											</Tooltip>			
										}
									/>
								)}
								<div style={{ display: 'flex', flexDirection: 'row' }}>
									<div className='AgronomicInputsButtonSection' style={{ marginTop: 7, marginBottom: 7}}>
										<Button
											disabled={false}
											className='AgronomicInputs_Button'
											style={{ height: 40, minWidth: 160, marginRight: 15 }}
											variant='outlined'
											onClick={() => setIsAgronomicInputsModalOpen(true)}
										>
										Agronomic Inputs
										</Button>
									</div>
									{/** Show any filters applied to the products list. */}
									{recommendationOptions?.isFilterApplied && productFilters && (
										<AppliedProductFilters filters={productFilters} />
									)}
								</div>
								{/** Series table */}
								<div className='MainEditorSelectedProducts'>
									{selectedSeriesList.filter(s => seriesMatchesCropBrand(brandLookup, s, cropBrand)).map(series => (
										<div 
											key={series.SeriesId + 'PlanSeries'} 
											className='SelectedSeries' 
											style={{
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'stretch'
											}}
										>
											<SeriesRow
												key={series.SeriesId}
												series={series}
												isGoldenHarvestUser={isGoldenHarvestUser}
												pickerContent={cropBrand.cropId === cornId && (
													<div style={{display:'flex', flexDirection:'column', minWidth:185}}>
														{Object.values(hybridLookup)
															.filter(h => h.IsAvailable && h.SeriesId === series.SeriesId)
															.map(h =>
															{
																return (
																	<Tooltip key={h.HybridId} title={`Add another row for ${h.HybridName}`}>
																		<Button 
																			style={{marginBottom: 8, display:'flex', flexDirection: 'row', alignItems:'center'}} 
																			variant='outlined' 
																			className={`AddHybridButton AddHybridButton-${h.HybridId}`}
																			onClick={() => addTraitToSeries(h.HybridId)}>
																			{!isGoldenHarvestUser && <AvailabilityIcon availability={h.Availability} style={{ width: 40, }}/>}  {h.HybridName}
																		</Button>
																	</Tooltip>
																);
															})}
													</div>
												)}
												pickerTitle={cropBrand.cropId === cornId ? 'Add Trait (s)' : undefined}
												onLocalPositioning={() => openLocalPositioningDrawer(series.SeriesId)}
												onDelete={() => dispatch(removeSeries({ planId: planId, seriesId: series.SeriesId, }))}
											/>
											{Object.values(selectedProducts).filter(p => p.seriesId === series.SeriesId && seriesMatchesCropBrand(brandLookup, series, cropBrand)).map(p => 
											{
												const treatment = treatmentLookup[treatmentKey(p)];
												const availableTreatments = Object.values(treatmentLookup)
													.filter(t => t.IsAvailable && t.SeriesId === p.seriesId && t.HybridId === p.hybridId)
													.map(t => ({ 
														treatmentKey: t.TreatmentKey, 
														treatmentName: t.TreatmentName,
														availability: t.Availability,
													}));
												return (
													<ProductRow
														key={p.key}
														showPricing={allowPricing}
														availability={treatmentLookup[treatmentKey(p)].Availability}
														hybridName={hybridLookup[p.hybridId].HybridName}
														treatmentName={treatment.TreatmentName}
														treatmentKey={treatmentKey(treatment)}
														treatments={availableTreatments}
														cropConfig={CropConfig()[p.cropId]}
														fetchRatesError={plantingRateError}
														price={{ 
															price: p.pricePerUnit, 
															pricingUnit: p.priceUnit,
														}}
														targetYield={p.targetYield === 0 ? undefined : p.targetYield}
														appliedAcres={p.appliedAcres === 0 ? undefined : p.appliedAcres}
														quantity={p.quantity === 0 ? undefined : p.quantity}
														seedingRate={p.seedingRate === 0 ? undefined : p.seedingRate}
														totalPrice={p.totalPrice === 0 ? undefined : p.totalPrice}
														onRefreshRates={() => 
														{
															if (plantingRatesIsLoading)
																return;

															// This must match the generalized planting rates call.
															getAllPlantingRates(dispatch, seriesLookup!);
														}}
														onUpdateTreatment={treatmentKey => 
														{
															dispatch(updateTreatment({ planId, productKey: p.key, treatmentKey }));
														}}
														onUpdate={u => 
														{
															dispatch(updateProductLine({
																planId: planId,
																productKey: p.key,
																plantingRates,
																line: {
																	appliedAcres: u.appliedAcres,
																	quantity: u.quantity,
																	seedingRate: u.seedingRate,
																	targetYield: u.targetYield,
																
																}
															}));
														}}
														onDelete={() => dispatch(removeProductLine({ planId: planId, key: p.key }))}
														isGoldenHarvestUser={isGoldenHarvestUser}
													/>
												);
											})}
										</div>
									))}
								</div>
							</div>
							{/** Prior year data collapsible section. */}
							<div
								className='MainEditorPriorYearData'
								style={{
									marginTop: 20,
									borderTop: `2px solid ${theme.colors.lightGrey}`,
									display: 'flex',
									flexDirection: 'column',
									flex: '1 1 auto',
									overflow: 'hidden',
									paddingBottom: 35,
									minHeight: !allLastYearSeeds.length ? 'inherit' : 160,
									maxHeight: !allLastYearSeeds.length ? 'inherit' : 200,
								}}
							>
								{allLastYearSeeds.length ?
									<PriorYearSeedList
										available={lastYearAvailableSeeds}
										unavailable={lastYearUnavailableSeeds}
										isAlreadyInPlan={oldSeed =>
										{
											const hybridId = oldSeedHybridId(oldSeed);
											return Object.values(selectedProducts).some(p => p.hybridId === hybridId);
										}}
										onAddToPlan={onAddOldSeed}
										isGoldenHarvestUser={isGoldenHarvestUser}
									/>
									:
									<span style={{ fontSize: theme.fontSizes.large, fontWeight: theme.fontWeights.bold }}>No Prior Year Order Data</span>
								}
							</div>
							<ApplyRecommendationModal
								open={showRunRecWarning}
								onCancel={() => setShowRunRecWarning(false)}
								onContinue={() => 
								{ 
									fetchAndApplyRecommendation(); 
									setShowRunRecWarning(false); 
								}}
							/>
							{recommendationOptions && (
								<AgronomicsModal
									applyFilter={onAgModalApply}
									clearFilter={onAgModalClear}
									brandApplication={cropBrand.brandApplication}
									closeModal={() => setIsAgronomicInputsModalOpen(false)}
									cropName={cropBrand.cropName}
									currentRecommendationOptions={recommendationOptions}
									defaultMin={rmMinMax.min}
									defaultMax={rmMinMax.max}
									isOpen={isAgronomicInputsModalOpen}
									uniqueTraits={!!uniqueTraits && uniqueTraits.map(t => ({ 
										TraitId: t.traitId,
										TraitName: t.traitName,
										Selected: recommendationOptions!.selectedTraits.some(s => s === t.traitId)
									}))}
									updateConsistency={recOptionsUpdatePerformance}
									updateFungicide={recOptionsUpdateFungicide}
									updateIrrigation={recOptionsUpdateIrrigation}
									updateMaxAndMinRm={recOptionsUpdateRmMinMax}
									updateMaxRm={recOptionsUpdateRmMax}
									updateMinRm={recOptionsUpdateRmMin}
									updateSelectedTraits={recOptionsUpdateTraitList}
									updateTopEndYield={recOptionsUpdateTopEndYield}
								/>
							)}
						</div>
					</>
				)}
				{seedsError && (
					<div style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						flex: 1,
					}}>
						The seeds list did not load. Try returning to the dashboard and navigating again.
						<Link to='/dashboard'>
							<Button style={{ height: 40, width: 200 }} variant='outlined' >
								Return to dashboard
							</Button>
						</Link>
					</div>
				)}
				{
					seriesLookup && localPositioningSeriesId && seriesLookup[localPositioningSeriesId] &&
					<LocalPositioningDrawer
						onClose={closeLocalPositioningDrawer}
						series={localPositioningSeriesId ? seriesLookup ? seriesLookup[localPositioningSeriesId] : undefined : undefined}
						session={session}
						visible={showLocalPositioningDrawer}
					/>
				}
				<ConvertModal
					open={isConvertingModalOpen}
					onCancel={() => setConvertingModalOpen(false)}
					onConvert={onConvert} 
				/>
			</ProductPlanEditorContent>
			<CoveringLoader
				className={anythingLoading ? 'loading' : ''}
				style={{backgroundColor: '#ddda', display: 'flex', flexDirection: 'column'}}
			>
				<ScaleLoader 
					color={theme.colors.primary} 
					loading={anythingLoading} 
				/>
				{/** Plan loading is covered by the global loader. */}

				{/** Show some specific messages as needed. */}
				{Object.entries(loadings).map(([m, l]) => l ? <div key={m}>{m}</div> : undefined)}
			</CoveringLoader>
		</div>
	);
};

const ProductPlanRecTabs = styled.div`
	display: flex;
	flex-direction: row;
	border-bottom: 1px solid ${themeGet('colors.lightGrey')};
	padding-left: 18px;
`;

const ProductPlanEditorContent = styled.div`
	display: flex;
	flex-direction: row;
	flex: 1 1 auto;
	overflow: hidden;
	height: 100%;
	
	.MainEditorVerticalStack {
		flex-grow: 1;
		display: flex;
		flex-direction: column;
		align-items: stretch;
		margin: 20px 0px 20px 57px;
		flex: 1 1 auto;
		overflow: hidden;

		.MainEditorTitle {
			font-family: ${themeGet('fonts.heading')};
			font-size: ${themeGet('fontSizes.large')};
			font-weight: ${themeGet('fontWeights.bold')};
			color: ${themeGet('colors.darkGrey')};
			display: flex;
		}

		.MainEditorContent {
			max-height: 700px;
			overflow-y: auto;
			display: flex;
			flex-direction: column;
			padding-right: 10px;
			padding-bottom: 20px;
		}
	}
`;

/** Does a series fall under a particular cropbrand? */
const seriesMatchesCropBrand = (brandLookup: BrandLookup, series: SeriesLookup[any], cropBrand: ICropBrandAssociation) => 
{
	const seriesBrand = brandLookup[series.BrandId];

	if (series.CropId !== cropBrand.cropId)
		return false;

	// Enogen cropId is corn, but it's treated as if it's own crop.
	const seriesIsEnogen = seriesBrand.Brand.toLowerCase().includes('enogen');
	if ((cropBrand.brandApplication === 'Enogen' && !seriesIsEnogen) || (cropBrand.brandApplication !== 'Enogen' && seriesIsEnogen))
		return false;

	return true;
};

const validateZipCode = (value: string) =>
{
	const zipReg = /^([0-9]{5})(?:-([0-9]{4}))?$/;
	const matches = zipReg.test(value);
	return matches;
};

/** Generate a list of the seeds from grower's old orders.  */
const lastYearGrowerSeeds = (
	grower: IGrowerResponse, 
	cropBrand: ICropBrandAssociation,
	currentCropYear: number, 
): IOrderLine[] => 
{
	if (!grower.OrdersByYear)
		return [];

	const lastYearOrders = grower.OrdersByYear[currentCropYear - 1];

	if (!lastYearOrders)
		return [];

	const cropName = cropBrand.cropName.toLowerCase();
	const cropType = cropBrand.cropType.toLowerCase();

	return lastYearOrders
		.flatMap(order => order.Seeds)
		.filter(seed =>
			seed.CropFamily && seed.Brand
			&& cropName.includes(seed.CropFamily.toLowerCase())
			&& (
				cropType.includes(seed.Brand.toLowerCase())
				// Special case enogen as the brand in CropEdge seems to be 'EG'
				|| seed.Brand.toLowerCase() == 'eg' && cropType.includes('enogen')
			)
			&& seed.HybridId && seed.SeriesId
		) ?? [];
};

/** Match old seeds to new seeds and group by availability. */
const groupLastYearSeedsByAvailability = (
	hybridLookup: HybridLookup,
	oldSeeds: IOrderLine[]
) => 
{
	return oldSeeds.reduce((acc,oldSeed) => 
	{
		const oldHybridId = oldSeedHybridId(oldSeed);
		const currentHybrid = hybridLookup[oldHybridId];
		if (currentHybrid) 
		{
			const alreadyAdded = acc.available.find(s => oldSeedHybridId(s) === oldHybridId);
			if (alreadyAdded)
				alreadyAdded.Quantity += oldSeed.Quantity;
			else
				acc.available.push({ ...oldSeed });
		}
		else
		{
			const alreadyAdded = acc.unavailable.find(s => oldSeedHybridId(s) === oldHybridId);
			if (alreadyAdded)
				alreadyAdded.Quantity += oldSeed.Quantity;
			else
				acc.unavailable.push({ ...oldSeed });
		}

		return acc;
	}, { 
		available: [], 
		unavailable: [] 
	} as { 
		available: IOrderLine[]; 
		unavailable: IOrderLine[]; 
	});
};

/** Old seed data includes a current equivalent which has precedence when finding the hybridId for the old seed. */
const oldSeedHybridId = (oldSeed: IOrderLine) => oldSeed.CurrentEquivalent ?
	oldSeed.CurrentEquivalent.HybridId : oldSeed.HybridId;

/** 
 * Handle all the logic associated with create or loading a plan. 
 * This is primarily here to keep the component clear and easy to read.
 * 
 * We enter 'create' mode when we land on this page with no selected plan.
 * 
 * @param trace Dev only. Turn on when debugging issues with plan loading.
 */
function useLoadOrCreatePlan(seedsLoaded: boolean, selectedGrower: IGrowerResponse, brandLookup: BrandLookup | undefined, trace: boolean = false)
{
	const dispatch = useAppDispatch();

	const selectedPlanId = useAppSelector(s => s.productGamePlanV7.selectedPlanId);
	// If we need to create this will be the Id.
	const [newPlanId,] = useState(() => uuidv4());

	// If the user started working on a new plan, but refreshed the page.
	const lastEditedNewPlanId = useAppSelector(s => 
	{
		const newPlans = Object.values(s.productGamePlanV7.plans).filter(p => p.isNewPlan);
		if (newPlans.length > 1)
			console.warn('An unusual number of new plans were found. Expected 1.', newPlans);

		return newPlans[0]?.planId;
	});

	const planId = selectedPlanId ?? lastEditedNewPlanId ?? newPlanId;
	
	// Setup amplitude tracking
	const trackingData = useMemo<EventTags>(() => ({

		[EventSelectionTags.GrowerId]: selectedGrower?.Id,
		[EventGrowerTags.GrowerName]: selectedGrower?.Name,
		[EventGrowerTags.GrowerSalesforceId]: selectedGrower?.SalesforceId,
		[EventGrowerTags.GrowerFoundationId]: selectedGrower?.FoundationId,

		[EventSelectionTags.PlanId]: planId,
		[EventPlanTags.PlanType]: 'Acre',

	}), [planId, selectedGrower]);

	const session = useScopedSession(planId, trackingData, {
		[EventStructureTags.PageContext]: 'acre_plan',
		[EventStructureTags.PageUrl]: window.location.toString()
	});
	const trackViewAcreProposal = useDynamicEvent(session);

	const plan = useAppSelector(s => s.productGamePlanV7.plans[planId]);
	const isLoadingPlan = useAppSelector(s => s.productGamePlanV7.isLoadingPlan);
	// There is only one flag for loading any plan. We hide that reality from the rest of this UI inside the hook. 
	const planWithLoading = useMemo(() => ({...plan, isLoading: isLoadingPlan}), [plan, isLoadingPlan]);

	function logTrace(message: string, relatedObjects?: RelatedObjects): void
	{
		if (!trace) return;

		const labeledMessage = `useLoadOrCreatePlan: ${message}`;
		if (relatedObjects)
			console.log(labeledMessage, relatedObjects);
		else
			console.log(labeledMessage);
	}

	useEffect(() => 
	{
		logTrace('Effect triggered');
		
		async function execute() 
		{
			if (!seedsLoaded || isLoadingPlan) 
				return;
			
			logTrace('execute() clause');

			// Load existing plan.
			if (selectedPlanId)
			{
				logTrace('Load existing plan clause');

				// If plan is already loaded skip.
				if (plan?.plan)
					return;

				logTrace('Loading plan');

				// Load Local Positioning
				await dispatch(getLocalPositioning({ requestedYear: undefined}));

				// Custom Treatments
				await dispatch(getCustomTreatments());

				await dispatch(getProductGamePlan({ 
					planId: selectedPlanId, 
					defaultTreatmentName: '',
				}));

				dispatch(loadPlanForEditing({ 
					planId, 
				}));

				trackViewAcreProposal(EventAcreProposalNames.ViewExistingAcreProposal, {
					[EventSelectionTags.PlanId]: selectedPlanId
				});
			}
			// Create a new plan when we enter this page with no selected planId.
			else
			{
				logTrace('New plan clause');

				// User was already editing a plan.
				if (plan?.edits)
					return;

				logTrace('Initializing new plan');

				if (!selectedGrower)
					throw new Error('Grower must be selected.');

				// Load Local Positioning
				await dispatch(getLocalPositioning({ requestedYear: undefined}));

				// Custom Treatments
				await dispatch(getCustomTreatments());

				dispatch(createNewPlan({
					planId: newPlanId,
					growerZip: selectedGrower.Zip,
				}));
				trackViewAcreProposal(EventAcreProposalNames.CreateNewAcreProposal);
			}
		}
		execute();
	}, [planId, selectedPlanId, seedsLoaded, isLoadingPlan, plan?.plan, plan?.edits]);

	const savePlan = async () => 
	{
		if (!brandLookup)
			throw new Error('Attempted to save a plan before seed list was loaded.');

		logTrace('savePlan() clause');

		if (plan.isNewPlan)
		{
			logTrace('New plan save clause');

			const result = await dispatch(createProductPlan({ 
				growerId: selectedGrower.Id,
				body: planEditsToApi(brandLookup, plan),
			})).unwrap();

			const newPlanId = result.id;

			// This will trigger the load existing plan useEffect.
			dispatch(setSelectedPlanId({planId: newPlanId}));
			return newPlanId;
		}
		else
		{ 
			logTrace('Existing plan save clause');

			await dispatch(putProductPlan({
				planId,
				body: planEditsToApi(brandLookup, plan),
			}));
			return planId;
		}
	};

	return {
		plan: planWithLoading,
		savePlan,
		planId,
		session,
	};
}

/** Convert rec options to a request to get seed rankings. */
const getSeedListFilteredRankings = (cropBrand: ICropBrandAssociation, options: IRecommendationOptions) =>
{
	return getFieldlessSeedRankings({
		acres: options.acres,
		consistencyPerf: options.performance,
		cropAssociation: `${cropBrand.cropId}${cropBrand.brandApplication}`,
		enogenOnly: cropBrand.brandApplication === 'Enogen',
		cropId: cropBrand.cropId,
		fungicide: options.fungicide,
		irrigated: options.irrigated,
		isFilterApplied: options.isFilterApplied,
		rmMax: options.rmMax,
		rmMin: options.rmMin,
		selectedTraits: options.selectedTraits,
		topEndYield: options.topEndYield,
		zipCode: options.zip
	});
};

/** Sort all selectable series (aka product list). */
const sortSelectableSeries = (
	hybridLookup: HybridLookup | undefined,
	selectableSeriesLookup: SeriesLookup | undefined,
	recommendationOptions: (IRecommendationOptions & {
		cropBrand: ICropBrandAssociation;
	}) | undefined,
	rankings?: IFieldlessRankingSeed[],
):IPlanProductListProps['seriesList'] => 
{
	if (!hybridLookup || !selectableSeriesLookup)
		return [];

	// Convert to UI type and sort by availability then RM
	return Object.values(
		mapObject(
			selectableSeriesLookup,
			(s, k) => ({
				...s,
				// Find rankings for all series.
				ranking: rankings?.find(r => r.SeedName.toLowerCase() === s.SeriesName.toLowerCase())?.Rank,
			})
		))
		// Apply recommendation options filters
		.filter(s => 
		{
			if (!recommendationOptions?.isFilterApplied)
				return true;

			const matchesSelectedTraits = recommendationOptions?.selectedTraits.length === 0 
				|| Object.values(hybridLookup).some(h => 
					h.SeriesId === s.SeriesId 
					&& recommendationOptions?.selectedTraits.includes(h.TraitId)
				);

			if (!matchesSelectedTraits)
				return false;

			const seriesRm = Number.parseFloat(s.RelativeMaturity);
			const { rmMin, rmMax } = recommendationOptions;
			const withinRmRange = rmMin === undefined || rmMax === undefined || 
        (seriesRm >= rmMin && seriesRm <= rmMax);
			
			if (!withinRmRange)
				return false;

			return true;
		})
		.map(s => ({
			id: s.SeriesId,
			name: s.SeriesName,
			availability: s.Availability,
			relativeMaturity: Number.parseFloat(s.RelativeMaturity),
			selectable: true,
			ranking: s.ranking,
		}))
		.sort((a, b) => 
		{
			// Sort by ranking. If ranking is missing, sort to bottom.
			const apiRanking = (a.ranking ?? -1) - (b.ranking ?? -1);
			if (apiRanking !== 0)
				return apiRanking;

			// Then sort by availability within the same ranking.
			const availRanking: Record<AvailabilityIndicator, number> = {
				['None']: 0,
				['Unknown']: 1,
				['Red']: 2,
				['Yellow']: 3,
				['Green']: 4,
			};

			const sortByAvail = availRanking[b.availability] - availRanking[a.availability];
			if (sortByAvail !== 0) 
				return sortByAvail;
					
			// Then sort by relative maturity within same availability
			return a.relativeMaturity - b.relativeMaturity;
		});
};
